// import packages
import { JSX, Suspense } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { LicenseInfo } from '@mui/x-license';
import dayjs from 'dayjs';
import AnnounceKit from 'announcekit-react';

// import config
import i18n from 'config/i18n';
import { setupAxios } from 'config/main';
import createConfiguredStore from 'config/reduxStore';
import localeNL from 'dayjs/locale/nl';

// import utils
import { setOrganisationPreferences } from 'redux/actionCreators';
import { getDateFnsLocale, getLocale } from 'utils/date';
import { setStore } from 'utils/reduxStore';

// import components
import BaseStyling from 'config/baseStyling';
import {
  FeatureDiscoveryStore,
  AppBar,
  NavigationRail,
  withProviders,
  Icon
} from '@sdb/planning-ui';
import { DesktopSteps } from 'components/shared/FeatureDiscovery/steps/desktop.steps';
import { MobileSteps } from 'components/shared/FeatureDiscovery/steps/mobile.steps';
import HourManagement from 'components/admin-portal/HourManagement';
import Realization from 'components/admin-portal/Procedures/Realization';
import Servicedesk from 'components/admin-portal/Procedures/Servicedesk';
import AdminSettings from 'components/admin-portal/Settings/Settings';
import DataQueue from 'components/admin-portal/queues/DataQueue';
import ImportQueue from 'components/admin-portal/queues/ImportQueue';
import QueueTabs from 'components/admin-portal/queues/QueueTabs';
import ReportQueue from 'components/admin-portal/queues/ReportQueue';
import SchedulingQueue from 'components/admin-portal/queues/SchedulingQueue';
import CostCalculationExternal from 'components/employee-card/cost-calculation-external/CostCalculationExternal';
import PlanUnitDistribution from 'components/employee-card/employment/planUnitDistribution/PlanUnitDistribution';
import EmployeeQualifications from 'components/employee-card/preferences/EmployeeQualifications/EmployeeQualifications';
import RegionalPoolEmploymentOrganisations from 'components/employee-card/preferences/RegionalPoolEmploymentOrganisations/RegionalPoolEmploymentOrganisations';
import TeamrosterEmployeesHeader from 'components/employee-portal/teamroster/RosterViewByEmployee/HeaderDojoWrapper';
import Teamroster from 'components/employee-portal/teamroster/Teamroster';
import WorkPatternPage from 'components/employee-portal/work-pattern/WorkPatternPage';
import FlexHistory from 'components/flex-portal/FlexHistory/FlexHistory';
import FlexAssignments from 'components/flex-portal/Assignments/FlexAssignments';
import RosterAssignments from 'components/roster-portal/Assignments/RosterAssignments';
import CreateEmployeeDialog from 'components/flex-portal/CreateEmployeeDialog/CreateEmployeeDialog';
import FlexMatchSettingsDialog from 'components/flex-portal/match/FlexMatchSettingsDialog/FlexMatchSettingsDialog';
import Administrator from 'components/roster-portal/Administrator/Administrator';
import RosterHome from 'components/roster-portal/home/Home';
import RosterPlan from 'components/roster-portal/RosterPlan/RosterPlan';
import OpenSelfMatchDialog from 'components/roster-portal/roster/open-requests/OpenSelfMatchDialog/OpenSelfMatchDialog';
import {
  Autocomplete,
  AutocompleteWithPreference,
  ColorPickerPopover,
  CreateDutyForAbsenceDialog,
  DeleteAbsenceDialog,
  MultiSelect,
  Select,
  showSnackbar
} from 'components/shared';
import BalanceBudget from 'components/shared/BalanceBudget/BalanceBudget';
import { CostCalculationExternalSettings } from 'components/shared/CostCalculationExternalSurcharge/CostCalculationExternalSettings';
import DutyCodesGrid from 'components/shared/DutyCodesGrid/DutyCodesGrid';
import EmployeeSelector from 'components/shared/EmployeeSelector';
import PlanGroupsDialog from 'components/shared/PlanGroups/PlanGroupsDialog';
import PlanUnitMultiSelect from 'components/shared/PlanUnitMultiSelect';
import PlanUnitSelector from 'components/shared/PlanUnitSelector';
import PreferredEmployees from 'components/shared/PreferredEmployees';
import PreferredEmployeesDialog from 'components/shared/PreferredEmployeesDialog';
import ShareCalendarDialog from 'components/shared/ShareCalendarDialog';
import GeneratedReports from 'components/shared/reports/GeneratedReports';

import BaseTabsRouting from './components/admin-portal/BaseTabsRouting/BaseTabsRouting';
import {
  grantsHeaders,
  grantsViews
} from './components/admin-portal/Grants/tabsConfig';
import {
  procedureHeaders,
  procedureTableViews
} from './components/admin-portal/Procedures/tabsConfig';
import {
  settingsHeaders,
  settingsViews
} from './components/admin-portal/Settings/tabsConfig';
import AuxiliaryValues from './components/admin-portal/Tables/AuxiliaryValues/AuxiliaryValues';
import MaxCLASettings from './components/admin-portal/Tables/CLA/MaxCLASettings/MaxCLASettings';
import PremiumPay from './components/admin-portal/Tables/CLA/PremiumPay';
import SalaryScales from './components/admin-portal/Tables/CLA/SalaryScales/SalaryScales';
import DHTClaSettings from './components/admin-portal/Tables/Settings/DHTClaSettings/DHTClaSettings';
import Holidays from './components/admin-portal/Tables/Year/Holidays/Holidays';
import { adminTableHeaders } from 'components/admin-portal/Tables/adminTableHeaders.const';
import { adminTableViews } from 'components/admin-portal/Tables/adminTableViews.const';
import TravelDistances from './components/admin-portal/TravelDistances';
import AdditionalPropertiesGrid from './components/employee-card/employment/additionalProperties/AdditionalPropertiesGrid';
import EmploymentHours from './components/employee-card/employment/EmploymentHours';
import TravelDistancesOverview from './components/employee-card/employment/travel-distances-overview/TravelDistancesOverview';
import RegionalPoolRequests from './components/regional-pool-portal/RegionalPoolRequests';
import ActivityPlanning from './components/roster-portal/activity-planning/ActivityPlanning';
import CapacityPlan from './components/roster-portal/capacity/CapacityPlan';
import ConnectSelfMatchDutiesDialog from './components/roster-portal/roster/open-requests/ConnectSelfMatchDutiesDialog/ConnectSelfMatchDutiesDialog';
import AbsenceNotices from './components/shared/AbsenceNotices/AbsenceNotices';

import { BalanceBudgetGiftProcedure } from 'components/admin-portal/Procedures/LeaveProcedures/components/BalanceBudgetGiftProcedure';
import { TravelExpenseClaims } from 'components/roster-portal/home/travel-expense-claims';

import './config/App.scss';
import './config/i18n';
import EmployeeMessages from 'components/employee-portal/messages/Messages';
import Notes from 'components/employee-portal/notes/Notes';
import EmployeeLeaveRequests from 'components/employee-portal/leave-requests/EmployeeLeaveRequests';
import Preferences from 'components/roster-portal/plan-units/Preferences/Preferences';
import Clusters from 'components/roster-portal/plan-units/Clusters/Clusters';
import FormationAndSortorder from 'components/roster-portal/plan-units/FormationAndSortorder/FormationAndSortorder';
import LeaveRequests from 'components/employee-portal/leave-requests/LeaveRequests';
import EmployeeBonuses from 'components/employee-card/employment/employee-bonuses/EmployeeBonuses';
import Settings from 'components/roster-portal/plan-units/Settings/Settings';
import TravelMovements from 'components/employee-card/employment/travel-movements/TravelMovements';
import TravelArrangements from 'components/employee-portal/travel-arrangements/TravelArrangements';
import DayLimitArrangementsForEmployee from 'components/employee-portal/day-limit-arrangements/DayLimitArrangementsForEmployee';
import PlanUnitsHome from 'components/roster-portal/plan-units/PlanUnitsHome';
import CareReceiversHome from 'components/roster-portal/care-receivers/CareReceiversHome';
import TenantFooterBar from 'components/shared/TenantFooterBar/TenantFooterBar';
import MultiResourcePlanning from 'components/mrp';
import SynchronisationLogs from 'components/logs/components/SynchronisationLogs/SynchronisationLogs';
import ImportSynchronisationLogs from 'components/logs/components/ImportSynchronisationLogs/ImportSynchronisationLogs';
import AppointmentsOverview from 'components/roster-portal/appointments-overview/AppointmentsOverview';
import Backlog from 'components/roster-portal/backlog';
import { FeatureDiscoveryGate } from 'components/shared/FeatureDiscovery/FeatureDiscoveryGate';

// import theme
import { ThemeProvider } from '@mui/material/styles';
import theme from 'config/theme';

// types
export { FeatureDiscoveryWithProviders } from 'components/shared/FeatureDiscovery/FeatureDiscoveryWithProviders';
import HoursTable from 'components/employee-card/employment/hours-table/HoursTable';
import FlexMatch from 'components/flex-portal/FlexMatch/FlexMatch';
import { NpsClientDialog } from 'components/shared/NpsClient/NpsClientDialog';
import type { CustomNavigationRailMoleculePropTypes } from '@sdb/planning-ui/ui-3/products/planning/src/components/molecules/NavigationRail/NavigationRail.types';

const AppBarWithProviders = withProviders(AppBar);
const NavigationRailWithProviders = withProviders<
  JSX.IntrinsicAttributes & CustomNavigationRailMoleculePropTypes
>(NavigationRail);

LicenseInfo.setLicenseKey(
  'b55b2921bfe508419a0cd5f3c6113404Tz0xMzA4MTUsRT0xODE1NjUyODAwMDAwLFM9cHJvLExNPXBlcnBldHVhbCxQVj1RMS0yMDI2LFE9MSxBVD1tdWx0aSxLVj0y'
);

setupAxios();

const ORG_PREFERENCES_SESSION_KEY = 'organisationPreferences';

const readCachedOrganisationPreferences = () => {
  try {
    const raw = sessionStorage.getItem(ORG_PREFERENCES_SESSION_KEY);
    if (!raw || raw === 'null') {
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const createNewStore = () => {
  const store = createConfiguredStore();
  window.reduxStore = store;
  return store;
};

let store = createNewStore();
setStore(store);

// Seed timezone before any React screen formats times (covers refresh races
// where duty/table data returns before /public/org-preferences).
const cachedOrganisationPreferences = readCachedOrganisationPreferences();
if (cachedOrganisationPreferences) {
  store.dispatch(setOrganisationPreferences(cachedOrganisationPreferences));
}

type ReactRoots = Record<string, Root>;
const reactRoots: ReactRoots = {};

const unmountComponentAtRootId = (rootId) => {
  const root = reactRoots[rootId];

  if (root) {
    root.unmount();
  }

  delete reactRoots[rootId];
};

window._unmountComponentAtRootId = unmountComponentAtRootId;

window.addEventListener('userLoggedOut', () => {
  for (const key of Object.keys(reactRoots)) {
    if (reactRoots[key]) {
      unmountComponentAtRootId(key);
      delete reactRoots[key];
    }
  }

  const { organisationPreferences } = store.getState();
  store = createNewStore();
  store.dispatch(setOrganisationPreferences(organisationPreferences));
  setStore(store);
});

window._getReactUtils = () => ({
  DesktopSteps,
  MobileSteps,
  FeatureDiscoveryStore
});

const t = i18n.getFixedT(getLocale(), 'common');
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      cacheTime: 0,
      onError: () => {
        showSnackbar({
          message: t('somethingWentWrong'),
          severity: 'error'
        });
      }
    }
  }
});

dayjs.locale(localeNL);

// TODO: After migration is done, move Suspense to ROOT node, to handle translation fallback
window._renderComponent = (
  componentName: string,
  domNode: HTMLElement,

  props: any = {},
  rootId: string
) => {
  if (!domNode) {
    domNode = document.createElement('div');
    document.body.appendChild(domNode);
  }

  let root;

  if (reactRoots[rootId]) {
    root = reactRoots[rootId];
  } else {
    root = createRoot(domNode);
    reactRoots[rootId] = root;
  }

  const renderComponent = () => {
    root.render(
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={getDateFnsLocale()}
      >
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <ThemeProvider theme={theme}>
              <BaseStyling>
                <Suspense fallback={<></>}>
                  {(function RenderComponent() {
                    switch (componentName) {
                      case 'GeneratedReports':
                        return <GeneratedReports />;
                      case 'DutyCodesGrid':
                        return <DutyCodesGrid {...props} />;
                      case 'Preferences':
                        return <Preferences {...props} />;
                      case 'Settings':
                        return <Settings {...props} />;
                      case 'FormationAndSortorder':
                        return <FormationAndSortorder {...props} />;
                      case 'Clusters':
                        return <Clusters {...props} />;
                      case 'RosterHome':
                        return <RosterHome />;
                      case 'Teamroster':
                        return <Teamroster />;
                      case 'LeaveRequests':
                        return <LeaveRequests {...props} />;
                      case 'OpenSelfMatchDialog':
                        return (
                          <OpenSelfMatchDialog
                            {...props}
                            onHidden={() => unmountComponentAtRootId(rootId)}
                          />
                        );
                      case 'ConnectSelfMatchDutiesDialog':
                        return (
                          <ConnectSelfMatchDutiesDialog
                            {...props}
                            onHidden={() => unmountComponentAtRootId(rootId)}
                          />
                        );
                      case 'FlexMatchSettingsDialog':
                        return (
                          <FlexMatchSettingsDialog
                            {...props}
                            onHidden={() => unmountComponentAtRootId(rootId)}
                          />
                        );
                      case 'PlanGroupsDialog':
                        return (
                          <PlanGroupsDialog
                            onHidden={() => unmountComponentAtRootId(rootId)}
                          />
                        );
                      case 'MultiSelect':
                        return <MultiSelect {...props} />;
                      case 'PlanUnitMultiSelect':
                        return <PlanUnitMultiSelect {...props} />;
                      case 'PlanUnitSelector':
                        return <PlanUnitSelector {...props} />;
                      case 'Select':
                        return <Select {...props} />;
                      case 'Autocomplete':
                        return <Autocomplete {...props} />;
                      case 'AutocompleteWithPreference':
                        return <AutocompleteWithPreference {...props} />;
                      case 'EmployeeSelector':
                        return <EmployeeSelector {...props} />;
                      case 'PlanUnitsHome':
                        return <PlanUnitsHome {...props} />;
                      case 'CareReceiversHome':
                        return <CareReceiversHome {...props} />;
                      case 'EmployeeQualifications':
                        return <EmployeeQualifications {...props} />;
                      case 'PreferredEmployees':
                        return <PreferredEmployees {...props} />;
                      case 'PreferredEmployeesDialog':
                        return (
                          <PreferredEmployeesDialog
                            {...props}
                            onHidden={() => unmountComponentAtRootId(rootId)}
                          />
                        );
                      case 'RegionalPoolEmploymentOrganisations':
                        return (
                          <RegionalPoolEmploymentOrganisations {...props} />
                        );
                      case 'WorkPatternPage':
                        return <WorkPatternPage {...props} />;
                      case 'EmployeeLeaveRequests':
                        return <EmployeeLeaveRequests {...props} />;
                      case 'BalanceBudget':
                        return <BalanceBudget {...props} />;
                      case 'EmployeeMessages':
                        return <EmployeeMessages {...props} />;
                      case 'EmployeeNotes':
                        return <Notes {...props} />;
                      case 'TravelArrangements':
                        return <TravelArrangements {...props} />;
                      case 'DayLimitArrangementsForEmployee':
                        return <DayLimitArrangementsForEmployee {...props} />;
                      case 'TeamrosterEmployeesHeader':
                        return <TeamrosterEmployeesHeader {...props} />;
                      case 'AdminSettings':
                        return <AdminSettings {...props} />;
                      case 'CreateEmployeeDialog':
                        return (
                          <CreateEmployeeDialog
                            {...props}
                            onHidden={() => unmountComponentAtRootId(rootId)}
                          />
                        );
                      case 'CapacityPlan':
                        return (
                          <CapacityPlan
                            {...props}
                            onHidden={() => unmountComponentAtRootId(rootId)}
                          />
                        );
                      case 'PlanUnitDistribution':
                        return <PlanUnitDistribution {...props} />;
                      case 'ShareCalendarDialog':
                        return <ShareCalendarDialog />;
                      case 'RosterAssignments':
                        return <RosterAssignments />;
                      case 'FlexHistory':
                        return <FlexHistory />;
                      case 'FlexAssignments':
                        return <FlexAssignments />;
                      case 'QueueTabs':
                        return (
                          <QueueTabs
                            {...props}
                            onHidden={() => unmountComponentAtRootId(rootId)}
                          />
                        );
                      case 'ImportQueue':
                        return <ImportQueue />;
                      case 'DataQueue':
                        return <DataQueue />;
                      case 'ReportQueue':
                        return <ReportQueue />;
                      case 'SchdulingQueue':
                        return <SchedulingQueue />;
                      case 'HourManagement':
                        return <HourManagement />;
                      case 'Realization':
                        return <Realization />;
                      case 'ServicedeskProcedures':
                        return <Servicedesk />;
                      case 'ColorPickerPopover':
                        return <ColorPickerPopover {...props} />;
                      case 'DeleteAbsenceDialog':
                        return <DeleteAbsenceDialog {...props} />;
                      case 'CreateDutyForAbsenceDialog':
                        return <CreateDutyForAbsenceDialog {...props} />;
                      case 'Snackbar':
                        if (props.icon) {
                          if (props.icon === 'info') {
                            props.icon = (
                              <Icon type="circle-info" color="primary" />
                            );
                          } else {
                            delete props.icon;
                          }
                        }
                        showSnackbar({ ...props });
                        break;
                      case 'ActivityPlanning':
                        return <ActivityPlanning {...props} />;
                      case 'TravelDistances':
                        return <TravelDistances />;
                      case 'TravelDistancesOverview':
                        return <TravelDistancesOverview {...props} />;
                      case 'RegionalPoolRequests':
                        return <RegionalPoolRequests {...props} />;
                      case 'DHTClaSettings':
                        return <DHTClaSettings />;
                      case 'AbsenceNotices':
                        return <AbsenceNotices {...props} />;
                      case 'CostCalculationExternalSettings':
                        return <CostCalculationExternalSettings {...props} />;
                      case 'CostCalculationExternal':
                        return <CostCalculationExternal {...props} />;
                      case 'AdditionalPropertiesGrid':
                        return <AdditionalPropertiesGrid {...props} />;
                      case 'EmploymentHours':
                        return <EmploymentHours {...props} />;
                      case 'SalaryScales':
                        return <SalaryScales />;
                      case 'RosterAdministrator':
                        return <Administrator {...props} />;
                      case 'MultiResourcePlanning':
                        return <MultiResourcePlanning {...props} />;
                      case 'AppointmentsOverview':
                        return <AppointmentsOverview {...props} />;
                      case 'Backlog':
                        return <Backlog {...props} />;
                      case 'SynchronisationLogs':
                        return <SynchronisationLogs {...props} />;
                      case 'ImportSynchronisationLogs':
                        return <ImportSynchronisationLogs {...props} />;
                      case 'AdminTables':
                        return (
                          <BaseTabsRouting
                            {...props}
                            headers={adminTableHeaders}
                            views={adminTableViews}
                            userGrantNeeded="hasAccessToAdminTablesReact"
                          />
                        );
                      case 'AdminProcedures':
                        return (
                          <BaseTabsRouting
                            {...props}
                            headers={procedureHeaders}
                            views={procedureTableViews}
                            userGrantNeeded="hasAccessToAdminProceduresReact"
                          />
                        );
                      case 'AdminSettingsReact':
                        return (
                          <BaseTabsRouting
                            {...props}
                            headers={settingsHeaders}
                            views={settingsViews}
                            userGrantNeeded="hasAccessToAdminSettingsReact"
                          />
                        );
                      case 'AdminGrants':
                        return (
                          <BaseTabsRouting
                            {...props}
                            headers={grantsHeaders}
                            views={grantsViews}
                            userGrantNeeded="hasAccessToAdminGrantsReact"
                          />
                        );
                      case 'PremiumPay':
                        return <PremiumPay {...props} />;
                      case 'FlexMatchReact':
                        return <FlexMatch {...props} />;
                      case 'RosterPlanReact':
                        return <RosterPlan {...props} />;
                      case 'Holidays':
                        return <Holidays {...props} />;
                      case 'HoursTable':
                        return <HoursTable {...props} />;
                      case 'MaxBuh':
                        return <MaxCLASettings {...props} />;
                      case 'AuxiliaryValues':
                        return <AuxiliaryValues {...props} />;
                      case 'BalanceBudgetGiftProcedure':
                        return <BalanceBudgetGiftProcedure />;
                      case 'TravelExpenseClaims':
                        return (
                          <TravelExpenseClaims
                            disableActions={true}
                            {...props}
                          />
                        );
                      case 'AnnounceKit':
                        return <AnnounceKit {...props} />;
                      case 'EmployeeBonuses':
                        return <EmployeeBonuses {...props} />;
                      case 'TravelMovements':
                        return <TravelMovements {...props} />;
                      // @sdb/planning-ui components
                      case 'AppBar':
                        return <AppBarWithProviders {...props} />;
                      case 'NavigationRail':
                        return <NavigationRailWithProviders {...props} />;
                      case 'NpsClient':
                        try {
                          const storeState = store.getState();
                          const userId = storeState?.userDetails?.accountId;
                          const tenantId = storeState?.tenantDetails?.tenantId;

                          if (!userId || !tenantId) {
                            return null;
                          }

                          return (
                            <NpsClientDialog
                              userId={userId}
                              tenantId={tenantId}
                            />
                          );
                        } catch (_) {
                          return null;
                        }
                      case 'FeatureDiscovery':
                        const storeState = store.getState();
                        const userId = storeState?.userDetails?.accountId;
                        const tenantId = storeState?.tenantDetails?.tenantId;

                        if (!userId || !tenantId) return null;

                        const environmentProps = {
                          userId: String(userId),
                          tenantId: String(tenantId)
                        };

                        return (
                          <FeatureDiscoveryGate
                            environmentProps={environmentProps}
                            {...props}
                          />
                        );
                      case 'TenantFooterBar':
                        return <TenantFooterBar {...props} />;
                      default:
                        break;
                    }
                  })()}
                </Suspense>
              </BaseStyling>
            </ThemeProvider>
          </Provider>
        </QueryClientProvider>
      </LocalizationProvider>
    );
  };

  renderComponent();

  return root;
};

// ✅ Dispatch event IMMEDIATELY after all globals are set
window.dispatchEvent(new Event('react-app-ready'));
